// ActivityHistoryTable.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Card,
  CircularProgress,
  TextField,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Popover,
  Typography,
  Grid, // Keep for Popover layout if needed, though less critical now
  Divider,
  GlobalStyles,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { DataGrid } from '@mui/x-data-grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ClearAllIcon from '@mui/icons-material/ClearAll'; // For the main clear button
import EventIcon from '@mui/icons-material/Event';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close'; // For the Popover clear button

import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import { grey } from '@mui/material/colors';

// --- MUI-inspired styles for react-datepicker (keep as is) ---
const datePickerGlobalStyles = (theme) => ({
  '.react-datepicker-popper': {
    zIndex: theme.zIndex.modal + 2,
  },
  '.react-datepicker': {
    fontFamily: theme.typography.fontFamily,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper, // Keep background as paper
  },
  '.react-datepicker__header': {
    backgroundColor: theme.palette.grey[100], // Light grey header
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
  },
  '.react-datepicker__current-month': {
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
  },
  '.react-datepicker__navigation': {
    top: theme.spacing(1.5),
  },
  '.react-datepicker__navigation-icon::before': {
    borderColor: theme.palette.text.secondary,
  },
  '.react-datepicker__day-name, .react-datepicker__day, .react-datepicker__time-name': {
    color: theme.palette.text.primary,
    width: '2.2rem',
    lineHeight: '2.2rem',
    margin: '0.1rem',
  },
  '.react-datepicker__day-name': {
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.caption.fontSize,
  },
  // Default day styling
  '.react-datepicker__day': {
    borderRadius: '50%',
    color: theme.palette.text.primary, // Default text color for days
    '&:hover': {
      backgroundColor: theme.palette.grey[200], // Light grey hover for non-selected days
      borderRadius: '50%',
    },
  },
  // Selected day (single or start/end of range)
  '.react-datepicker__day--selected, .react-datepicker__day--range-start, .react-datepicker__day--range-end': {
    backgroundColor: theme.palette.grey[400], // Medium grey for selected
    color: theme.palette.getContrastText(theme.palette.grey[400]), // Ensure text is readable
    borderRadius: '50%',
    '&:hover': {
      backgroundColor: theme.palette.grey[500], // Slightly darker grey on hover if already selected
    },
  },
  // Days within a selected range (not start or end)
  '.react-datepicker__day--in-range, .react-datepicker__day--in-selecting-range': {
    backgroundColor: theme.palette.grey[100], // Lighter grey for in-range days
    color: theme.palette.getContrastText(theme.palette.grey[200]), // Ensure text is readable
    borderRadius: '0', // For the "pill" effect
    '&:hover': {
      backgroundColor: theme.palette.grey[500], // Slightly darker grey on hover if already selected
    },
  },
  // Adjust pill effect for start/end of range
  '.react-datepicker__day--range-start:not(.react-datepicker__day--range-end)': {
    backgroundColor: theme.palette.grey[200], // Match selected color
    color: theme.palette.getContrastText(theme.palette.grey[400]),
    borderRadius: '50% 0 0 50%',
    '&:hover': {
      backgroundColor: theme.palette.grey[500],
    },
  },
  '.react-datepicker__day--range-end:not(.react-datepicker__day--range-start)': {
    backgroundColor: theme.palette.grey[200], // Match selected color
    color: theme.palette.getContrastText(theme.palette.grey[400]),
    borderRadius: '0 50% 50% 0',
    '&:hover': {
      backgroundColor: theme.palette.grey[300],
    },
  },
  // If a single day is selected as a range (start and end are the same)
  '.react-datepicker__day--range-start.react-datepicker__day--range-end': {
    backgroundColor: theme.palette.grey[400], // Match selected color
    color: theme.palette.getContrastText(theme.palette.grey[400]),
    borderRadius: '50%',
    '&:hover': {
      backgroundColor: theme.palette.grey[500],
    },
  },
  '.react-datepicker__day--today': {
    fontWeight: theme.typography.fontWeightBold,
  },
  '.react-datepicker__day--disabled': {
    color: theme.palette.text.disabled,
    cursor: 'default',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
});

function ActivityHistoryTable({ logs, isLoading, onRefreshLogs, currentTimeout, onUpdateTimeout }) {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const prevRowsRef = useRef(); // Keep track of the previous rows reference

  const [activityLogs, setActivityLogs] = useState([]);
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);
  const [timeoutValue, setTimeoutValue] = useState(currentTimeout.toString());

  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
  const exportMenuOpen = Boolean(exportMenuAnchorEl);

  const [dateRangeAnchorEl, setDateRangeAnchorEl] = useState(null);
  const dateRangePickerOpen = Boolean(dateRangeAnchorEl);
  const dateRangePickerId = dateRangePickerOpen ? 'date-range-popover' : undefined;

  useEffect(() => {
    setActivityLogs(logs || []);
  }, [logs]);

  useEffect(() => {
    setTimeoutValue(currentTimeout.toString());
  }, [currentTimeout]);

  const handleExportMenuClick = (event) => setExportMenuAnchorEl(event.currentTarget);
  const handleExportMenuClose = () => setExportMenuAnchorEl(null);

  const handleDateRangeIconClick = (event) => setDateRangeAnchorEl(event.currentTarget);
  const handleDateRangeClose = () => setDateRangeAnchorEl(null);

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates;
    setFilterStartDate(start);
    setFilterEndDate(end);
    // If both start and end dates are selected, close the popover
    if (start && end) {
      handleDateRangeClose();
    }
  };

  const handleClearDateRangeInPopover = () => {
    setFilterStartDate(null);
    setFilterEndDate(null);
    handleDateRangeClose(); // Also close the popover after clearing
  };

  // ... (export functions remain the same)
  const exportToExcel = (data, fileName = 'export.xlsx') => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, fileName);
  };

  const handleExportExcelClick = () => {
    const plainData = rows.map(({ view_details, id, ...rest }) => ({
      ...rest,
      timestamp: new Date(rest.timestamp).toLocaleString(),
    }));
    exportToExcel(plainData, 'activity-history.xlsx');
    handleExportMenuClose();
  };

  const exportToCSV = (data, fileName = 'export.csv') => {
    if (data.length === 0) return;
    const csvHeader = Object.keys(data[0]).join(',') + '\n';
    const csvRows = data
      .map((row) =>
        Object.values(row)
          .map((value) => `"${String(value).replace(/"/g, '""')}"`)
          .join(',')
      )
      .join('\n');
    const csvContent = csvHeader + csvRows;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, fileName);
  };

  const handleExportCsvClick = () => {
    const plainData = rows.map(({ view_details, id, ...rest }) => ({
      ...rest,
      timestamp: new Date(rest.timestamp).toLocaleString(),
    }));
    exportToCSV(plainData, 'activity-history.csv');
    handleExportMenuClose();
  };

  useEffect(() => {
    const buildRows = () => {
      const filteredLogs = activityLogs.filter((log) => {
        if (!log.timestamp) return false;
        const logDate = new Date(log.timestamp);
        const checkStartDate = filterStartDate ? new Date(new Date(filterStartDate).setHours(0, 0, 0, 0)) : null;
        const checkEndDate = filterEndDate ? new Date(new Date(filterEndDate).setHours(23, 59, 59, 999)) : null;

        if (checkStartDate && logDate < checkStartDate) return false;
        if (checkEndDate && logDate > checkEndDate) return false;
        return true;
      });

      const newRows = filteredLogs.map((log, index) => ({
        id: log._id || `log-${index}`,
        operation_type: log.operation_type,
        product_name: log.product_name,
        product_version: log.product_version,
        org_name: log.org_name,
        status: log.status,
        user: log.user?.email || log.user || 'N/A',
        timestamp: new Date(log.timestamp).toISOString(),
      }));
      setRows(newRows);
    };
    buildRows();
  }, [activityLogs, filterStartDate, filterEndDate]);

  const handleTimeoutChange = (event, newTimeout) => {
    if (newTimeout !== null) {
      setTimeoutValue(newTimeout);
      onUpdateTimeout(parseInt(newTimeout, 10));
    }
  };

  const columns = useMemo(
    () => [
      // ... (columns definition remains the same)
      { field: 'operation_type', headerName: 'Operation', flex: 0.8, minWidth: 100 },
      { field: 'product_name', headerName: 'Product', flex: 1.2, minWidth: 180 },
      { field: 'product_version', headerName: 'Version', flex: 0.6, minWidth: 80 },
      { field: 'org_name', headerName: 'Org', flex: 0.7, minWidth: 100 },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.5,
        minWidth: 80,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const status = params.value;
          let icon;
          let color = 'inherit';
          if (status === 'Success') {
            icon = <CheckCircleIcon />;
            color = 'success.main';
          } else if (status === 'In Progress') {
            icon = <CircularProgress size={20} sx={{ color: 'info.main' }} />;
            color = 'info.main';
          } else if (status === 'Failed') {
            icon = <CancelIcon />;
            color = 'error.main';
          } else {
            icon = <Box component="span">-</Box>;
          }
          return (
            <Tooltip title={status || 'Unknown'} placement="top">
              <Box sx={{ color }}>{icon}</Box>
            </Tooltip>
          );
        },
      },
      { field: 'user', headerName: 'User', flex: 1, minWidth: 150 },
      {
        field: 'timestamp',
        headerName: 'Date',
        flex: 1,
        minWidth: 170,
        renderCell: (params) => new Date(params.value).toLocaleString(),
      },
    ],
    []
  );

  return (
    <ArgonBox>
      <GlobalStyles styles={datePickerGlobalStyles(theme)} />
      <Card sx={{ borderRadius: 2, overflow: 'visible' }}>
        <ArgonBox p={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
          <ArgonTypography variant="h6" sx={{ mr: 'auto' }}>
            Activity History
          </ArgonTypography>

          {/* Read-only widget on dashboard can hide deletion controls by passing a no-op onUpdateTimeout.
              Keep UI but disable interaction when handler is empty. */}
          <Box display="flex" alignItems="center" gap={0.5} flexWrap="nowrap">
            <ArgonTypography
              variant="caption"
              fontWeight="medium"
              sx={{ color: 'text.secondary', whiteSpace: 'nowrap', mr: 0.5 }}
            >
              Log Deletion:
            </ArgonTypography>
            <ToggleButtonGroup
              color="primary"
              value={timeoutValue}
              exclusive
              onChange={onUpdateTimeout ? handleTimeoutChange : undefined}
              aria-label="Log Deletion Timeout"
              size="small"
              disabled={!onUpdateTimeout}
            >
              <ToggleButton value="24" aria-label="24 hours" sx={{ p: '4px 8px', lineHeight: 1 }}>
                <ArgonTypography variant="button" fontWeight="regular" sx={{ fontSize: '0.75rem' }}>
                  24H
                </ArgonTypography>
              </ToggleButton>
              <ToggleButton value="48" aria-label="48 hours" sx={{ p: '4px 8px', lineHeight: 1 }}>
                <ArgonTypography variant="button" fontWeight="regular" sx={{ fontSize: '0.75rem' }}>
                  48H
                </ArgonTypography>
              </ToggleButton>
              <ToggleButton value="0" aria-label="Custom/Never" sx={{ p: '4px 8px', lineHeight: 1 }}>
                <ArgonTypography variant="button" fontWeight="regular" sx={{ fontSize: '0.75rem' }}>
                  Never
                </ArgonTypography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 0.5, height: '28px', alignSelf: 'center' }} />

          <Box display="flex" alignItems="center" gap={0.5} flexWrap="nowrap">
            <Tooltip title="Filter by Date Range">
              <IconButton
                onClick={handleDateRangeIconClick}
                color="primary"
                size="medium"
                aria-describedby={dateRangePickerId}
              >
                <EventIcon />
              </IconButton>
            </Tooltip>
            <Popover
              id={dateRangePickerId}
              open={dateRangePickerOpen}
              anchorEl={dateRangeAnchorEl}
              onClose={handleDateRangeClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: 2,
                  background: 'white',
                },
              }}
            >
              <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <DatePicker
                  selected={filterStartDate}
                  onChange={handleDateRangeChange}
                  startDate={filterStartDate}
                  endDate={filterEndDate}
                  selectsRange
                  inline
                  monthsShown={1}
                />

                <Box>
                  <Button
                    onClick={() => {
                      setFilterStartDate(null);
                      setFilterEndDate(null);
                    }}
                    size="small"
                    variant="contained"
                    color="info"
                    sx={{ mt: 1 }}
                  >
                    Clear
                  </Button>
                </Box>
              </Box>
            </Popover>

            <Tooltip title="Clear Date Filters (Main)">
              <span>
                <IconButton
                  onClick={() => {
                    setFilterStartDate(null);
                    setFilterEndDate(null);
                  }}
                  color="default"
                  size="medium"
                  disabled={!filterStartDate && !filterEndDate}
                >
                  <ClearAllIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Refresh Logs">
              <IconButton onClick={onRefreshLogs} color="primary" size="medium">
                <AutorenewIcon />
              </IconButton>
            </Tooltip>

            <Button
              variant="contained"
              color="info"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExportMenuClick}
              sx={{ ml: 0.5 }}
            >
              Export
            </Button>
            <Menu anchorEl={exportMenuAnchorEl} open={exportMenuOpen} onClose={handleExportMenuClose}>
              <MenuItem onClick={handleExportCsvClick}>Export as CSV</MenuItem>
              <MenuItem onClick={handleExportExcelClick}>Export as Excel</MenuItem>
            </Menu>
          </Box>
        </ArgonBox>

        {isLoading && rows.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 400, p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ height: 500, p: 0.5 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              getRowId={(row) => row.id}
              density="compact"
              initialState={{
                sorting: {
                  sortModel: [{ field: 'timestamp', sort: 'desc' }],
                },
              }}
              sx={{
                border: 'none',
                width: '100%',
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: (theme) => theme.palette.grey[100],
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'medium',
                  fontSize: '0.8rem',
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '0.8rem',
                  borderBottom: (theme) => `1px solid ${theme.palette.grey[200]}`,
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                },
                '& .MuiTablePagination-root': {
                  display: 'flex',
                  justifyContent: 'center',
                },

                '& .MuiToolbar-root': {
                  width: 'min-content',
                  marginRight: '6rem',
                },
                '& .MuiInputBase-root': {
                  width: 'min-content !important',
                },
              }}
              disableSelectionOnClick
            />
          </Box>
        )}
      </Card>
    </ArgonBox>
  );
}

export default ActivityHistoryTable;
